import { OrderUnitOfMeasure } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import type { Ticket } from '~hooks/useTickets/models';

const KEY_PREFIX = 'approvals.ticket_details.scale_ticket';

const IS_WEIGHT_UNIT = {
  [OrderUnitOfMeasure.BAG]: false,
  [OrderUnitOfMeasure.BARREL]: false,
  [OrderUnitOfMeasure.BUSHEL]: false,
  [OrderUnitOfMeasure.CUBIC_METER]: false,
  [OrderUnitOfMeasure.EACH]: false,
  [OrderUnitOfMeasure.FOOT]: false,
  [OrderUnitOfMeasure.GALLON]: false,
  [OrderUnitOfMeasure.HOUR]: false,
  [OrderUnitOfMeasure.KILOMETER]: false,
  [OrderUnitOfMeasure.LITER]: false,
  [OrderUnitOfMeasure.LOAD]: false,
  [OrderUnitOfMeasure.METER]: false,
  [OrderUnitOfMeasure.MILE]: false,
  [OrderUnitOfMeasure.PALLET]: false,
  [OrderUnitOfMeasure.POUND]: true,
  [OrderUnitOfMeasure.TON]: true,
  [OrderUnitOfMeasure.TONNE]: true,
  [OrderUnitOfMeasure.YARD]: false,
} satisfies Record<OrderUnitOfMeasure, boolean>;

export const getChangedScaleTicketIds = (
  values: { machineId?: string; trailerId?: string },
  ticket?: Ticket,
) => ({
  machineId:
    values.machineId === (ticket?.externalMachineId ?? '') ? undefined : values.machineId,
  trailerId:
    values.trailerId === (ticket?.externalTrailerId ?? '') ? undefined : values.trailerId,
});

export const getScaleTicketRows = (ticket?: Ticket) => {
  const unit = ticket?.unitOfMeasure?.name;
  const withWeightUnit = (value?: string | null) => {
    if (value === null || value === undefined) {
      return '';
    }

    return IS_WEIGHT_UNIT[unit as OrderUnitOfMeasure] ? `${value} ${unit}` : value;
  };

  return [
    ['gross_weight', withWeightUnit(ticket?.externalGrossWeight)],
    ['tare_weight', withWeightUnit(ticket?.externalTareWeight)],
    ['load_number', ticket?.externalLoadNumber?.toString() ?? ''],
    ['weighmaster', ticket?.externalWeighmasterName ?? ''],
    ['scale_id', ticket?.externalScaleId ?? ''],
    ['silo_id', ticket?.externalSiloId ?? ''],
    ['mix_design_id', ticket?.externalMixDesignId ?? ''],
    ['item_id', ticket?.externalItemId ?? ''],
    [
      'cumulative_daily_quantity',
      withWeightUnit(ticket?.externalCumulativeDailyQuantity),
    ],
    ['overweight_weight', withWeightUnit(ticket?.externalOverweightWeight)],
    [
      'cumulative_daily_overweight_quantity',
      withWeightUnit(ticket?.externalCumulativeDailyOverweightQuantity),
    ],
  ].map(([key, value]) => ({ key, label: t(`${KEY_PREFIX}.${key}`), value }));
};

export const getScaleTicketEditableFields = (ticket?: Ticket) =>
  [
    {
      label: t(`${KEY_PREFIX}.trailer_id`),
      name: 'trailerId',
      value: ticket?.externalTrailerId ?? '',
    },
    {
      label: t(`${KEY_PREFIX}.machine_id`),
      name: 'machineId',
      value: ticket?.externalMachineId ?? '',
    },
  ] as const;
